/**
 * PlanPreview — visual preview of an AI-generated plan proposal.
 *
 * Shows tasks in a timeline-like layout with:
 * - Offset from anchor (e.g. "-30d", "-7d")
 * - Task titles
 * - Tags, checklist counts, time estimates
 * - Category grouping
 */

import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import type { AiPlanProposal, AiPlanStep } from "@totodo/operations"
import ChecklistIcon from "@/shared/icons/totodoicons/stroke/task-list.svg?react"
import ClockIcon from "@/shared/icons/totodoicons/stroke/calendar.svg?react"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatOffset(rule: string): string {
  // Parse "-30d>sat" → "30 days before"
  const match = rule.match(/^([+-]?)(\d+)(d|w|m|h)/)
  if (!match) {
    if (rule.startsWith("same_day")) return "Same day"
    return rule
  }
  const [, sign, num, unit] = match
  const n = parseInt(num!, 10)
  const unitLabel = unit === "d" ? (n === 1 ? "day" : "days")
    : unit === "w" ? (n === 1 ? "week" : "weeks")
    : unit === "m" ? (n === 1 ? "month" : "months")
    : (n === 1 ? "hour" : "hours")

  if (sign === "-" || sign === "") return `${n} ${unitLabel} before`
  return `${n} ${unitLabel} after`
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PlanPreviewProps {
  proposal: AiPlanProposal
  compact?: boolean
}

export const PlanPreview = memo(function PlanPreview({
  proposal,
  compact = false,
}: PlanPreviewProps) {
  const { t } = useTranslation()

  const sortedSteps = useMemo(
    () => [...proposal.steps].sort((a, b) => a.sequenceOrder - b.sequenceOrder),
    [proposal.steps],
  )

  return (
    <div className="flex flex-col gap-1">
      {/* Plan header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{proposal.name}</h4>
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {proposal.steps.length} {t("tasks")}
        </span>
      </div>

      {proposal.reasoning && !compact && (
        <p className="mb-1 text-xs text-muted-foreground">{proposal.reasoning}</p>
      )}

      {/* Steps timeline */}
      <div className="flex flex-col">
        {sortedSteps.map((step, i) => (
          <PlanStepRow
            key={`${step.sequenceOrder}-${i}`}
            step={step}
            isLast={i === sortedSteps.length - 1}
            compact={compact}
          />
        ))}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// PlanStepRow
// ---------------------------------------------------------------------------

function PlanStepRow({
  step,
  isLast,
  compact,
}: {
  step: AiPlanStep
  isLast: boolean
  compact: boolean
}) {
  return (
    <div className="flex gap-3">
      {/* Timeline line + dot */}
      <div className="flex flex-col items-center">
        <div className="mt-1.5 size-2 rounded-full bg-primary" />
        {!isLast && <div className="w-px flex-1 bg-border" />}
      </div>

      {/* Content */}
      <div className={cn("flex-1 pb-3", isLast && "pb-0")}>
        <div className="flex items-start gap-2">
          <span className="flex-1 text-sm font-medium leading-tight">
            {step.title}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatOffset(step.offsetRule)}
          </span>
        </div>

        {step.note && !compact && (
          <p className="mt-0.5 text-xs text-muted-foreground">{step.note}</p>
        )}

        {/* Meta row */}
        {!compact && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {step.tags?.map((tag) => (
              <span
                key={tag}
                className="rounded bg-accent px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {tag}
              </span>
            ))}
            {step.checklistItems && step.checklistItems.length > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <ChecklistIcon className="size-3" />
                {step.checklistItems.length}
              </span>
            )}
            {step.estimatedMinutes && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <ClockIcon className="size-3" />
                {formatMinutes(step.estimatedMinutes)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
